import React, { useState } from 'react';

import type { SelectAutocompleteItem, SelectAutocompleteResult } from '@zimpel/common/components/AutoComplete/index';
import { AutoCompleteContainer as AutoComplete } from './AutoCompleteContainer';
import AutoCompleteElement from './AutoCompleteElement';
import type { Props as AutoCompleteProps } from './AutoCompleteContainer';

type OnSelect = (item: SelectAutocompleteItem) => void;

export type SelectAutocompleteProps = {
  onGetOptions: (searchTerm: string) => Promise<SelectAutocompleteResult>;
  onSelect: OnSelect;
  filterItems?: (items: SelectAutocompleteItem[]) => SelectAutocompleteItem[];
  buildCustomActions?: ({ items, searchTerm }: { items: SelectAutocompleteItem[], searchTerm: string }) => SelectAutocompleteItem[];
  onSelectCustomAction?: (item: SelectAutocompleteItem, onSelect: OnSelect) => void;
} & Omit<AutoCompleteProps, 'getOptions' | 'items'>;

const isCustomActionItem = (item: SelectAutocompleteItem): boolean => item.action !== 'select';

const SelectAutocomplete: React.FC<SelectAutocompleteProps> = ({
  onGetOptions,
  onSelect,
  filterItems = (items): SelectAutocompleteItem[] => items,
  buildCustomActions = (): SelectAutocompleteItem[] => [],
  onSelectCustomAction = (): void => { /* DO NOTHING */ },
  ...props
}) => {
  const [autocompleteItems, setAutocompleteItems] = useState<SelectAutocompleteItem[]>([]);

  const getOptions = async (searchTerm: string) => {
    const response = await onGetOptions(searchTerm) || { result: [] };
    const items: SelectAutocompleteItem[] = response.result.map((item) => ({
      action: 'select',
      ...item,
    }));

    const combinedItems: SelectAutocompleteItem[] = [];
    if (buildCustomActions) {
      const customActionItems = buildCustomActions({ items, searchTerm });
      combinedItems.push(...customActionItems);
    }
    combinedItems.push(...items);

    setAutocompleteItems(combinedItems);
  };

  const mapOptions = () => (
    filterItems(autocompleteItems).map((item) => ({
      value: item.value,
      label: item.label,
      onSelect: (): void => {
        if (isCustomActionItem(item)) {
          onSelectCustomAction(item, onSelect);
        } else {
          onSelect(item);
        }
      },
      element: (
        <AutoCompleteElement
          icon={item.icon}
          borderBottom={isCustomActionItem(item)}
        >
          { item.label }
        </AutoCompleteElement>
      ),
    }))
  );

  return (
    <AutoComplete
      getOptions={getOptions}
      items={mapOptions()}
      {...props}
    />
  );
};

export default SelectAutocomplete;
